import { User } from 'lucide-react'
import { getImageUrl } from '@/features/identities/identity-image'
import { useAuthedImageUrl } from '@/hooks/use-authed-image-url'
import { cn } from '@/utils/cn'

/**
 * Identity avatar — face crop from the linked ID document, or a fallback
 * person icon when no photo has been extracted yet.
 *
 * The face image lives behind the auth-gated ``/api/v1/files/{key}`` endpoint
 * (the same endpoint that streams source documents). Browsers don't send
 * Bearer tokens on bare ``<img src>`` requests, so we fetch the bytes via
 * ``useAuthedImageUrl`` and render the resulting blob URL. The fallback
 * icon stays mounted underneath the image so it shows during fetch + on
 * fetch error without flashing.
 */
export function IdentityAvatar({
  photoPath,
  name,
  fallbackIconClassName = 'h-16 w-16 md:h-20 md:w-20',
}: {
  photoPath: string | undefined
  name: string
  /** Sizes the fallback icon: the header card wants a large one, list rows a small one. */
  fallbackIconClassName?: string
}) {
  const blobUrl = useAuthedImageUrl(getImageUrl(photoPath))
  return (
    <>
      {blobUrl ? (
        <img src={blobUrl} alt={name} className="w-full h-full object-cover relative z-10" />
      ) : null}
      <div
        className="w-full h-full flex items-center justify-center absolute inset-0"
        aria-hidden={blobUrl !== null}
      >
        <User className={cn('text-muted-foreground', fallbackIconClassName)} />
      </div>
    </>
  )
}
